import { z } from 'zod'
import { FadeCurve, GainDb, Id, Metres, Milliseconds } from './primitives.js'

/**
 * Mix groups. Listeners get per-bus faders, so someone can turn their music
 * down without losing the narration they came for.
 */
export const Bus = z.object({
  id: Id,
  name: z.string().max(60),
  gainDb: GainDb.default(0),
})
export type Bus = z.infer<typeof Bus>

export const DEFAULT_BUS_IDS = ['narration', 'ambience', 'music', 'sfx', 'live', 'ui'] as const

/**
 * "While anything on `sourceBus` is audible, pull `targetBus` down." Narration
 * and live voice duck ambience and music by default; without this the moment a
 * guide starts talking is the moment nobody can hear them.
 */
export const DuckingRule = z.object({
  sourceBusId: Id,
  targetBusId: Id,
  amountDb: z.number().finite().min(0).max(60),
  attackMs: Milliseconds.default(150),
  releaseMs: Milliseconds.default(600),
})
export type DuckingRule = z.infer<typeof DuckingRule>

export const RolloffKind = z.enum(['inverse', 'linear', 'exponential'])

/**
 * How loudness falls with distance. `refDistanceM` is the radius of full
 * volume and `maxDistanceM` the radius beyond which the source is culled;
 * the studio draws both as rings on the map, because creators reason about
 * circles on a map rather than about decibel curves.
 */
export const DistanceModel = z.object({
  refDistanceM: Metres.default(3),
  maxDistanceM: Metres.default(80),
  rolloff: RolloffKind.default('inverse'),
  rolloffFactor: z.number().finite().positive().default(1),
})
export type DistanceModel = z.infer<typeof DistanceModel>

/** A directional source: a PA horn down a hallway, a barker facing the midway. */
export const Directivity = z.object({
  /** Degrees clockwise from north that the source faces. */
  headingDeg: z.number().finite().min(0).lt(360).default(0),
  innerAngleDeg: z.number().finite().min(0).max(360).default(360),
  outerAngleDeg: z.number().finite().min(0).max(360).default(360),
  outerGainDb: GainDb.default(-12),
})
export type Directivity = z.infer<typeof Directivity>

const SingleAsset = z.object({
  kind: z.literal('asset'),
  assetId: Id,
})

const Playlist = z.object({
  kind: z.literal('playlist'),
  assetIds: z.array(Id).min(1),
  order: z.enum(['sequential', 'shuffle']).default('sequential'),
  crossfadeMs: Milliseconds.default(0),
  gapMs: Milliseconds.default(0),
})

/**
 * Weighted variants with per-play jitter. This is the antidote to repetition
 * fatigue — a bird that plays the identical take every ninety seconds stops
 * sounding like a bird — so it is a first-class source kind, not a workaround.
 */
const RandomContainer = z.object({
  kind: z.literal('random'),
  variants: z
    .array(
      z.object({
        assetId: Id,
        weight: z.number().finite().positive().default(1),
      }),
    )
    .min(1),
  pitchJitterSemitones: z.number().finite().min(0).max(12).default(0),
  gainJitterDb: z.number().finite().min(0).max(24).default(0),
  minIntervalMs: Milliseconds.default(0),
  maxIntervalMs: Milliseconds.default(0),
})

export const SoundSource = z.discriminatedUnion('kind', [SingleAsset, Playlist, RandomContainer])
export type SoundSource = z.infer<typeof SoundSource>

export const Fade = z.object({
  durationMs: Milliseconds.default(0),
  curve: FadeCurve.default('equalPower'),
})
export type Fade = z.infer<typeof Fade>
